import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Tooltip, Typography,
} from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CameraswitchIcon from "@mui/icons-material/Cameraswitch";
import { useFullScreenDialog } from "../useFullScreenDialog";

// A photo taken here never touches the device's storage. The frame is read off a
// live getUserMedia stream onto a canvas and handed back as an in-memory File, so
// no camera app is involved. The camera app is what writes a copy to the gallery:
// `<input capture>` hands off to it, and on Android whether that copy is kept is
// up to the camera app, not to us. For a clinical photograph that's the point. A
// copy left on a responder's phone is a patient record outside the system.
//
// getUserMedia needs a secure context (HTTPS, or localhost in dev) and the user's
// permission. Without either the dialog says so rather than offering a fallback
// that would save to the device.

const MAX_PHOTO_DIM = 1600;   // longest edge, matching RichText's downscale
const JPEG_QUALITY = 0.85;

export function cameraSupported() {
  return typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia
    && typeof window !== "undefined" && window.isSecureContext !== false;
}

function cameraError(err) {
  const name = err?.name;
  if (name === "NotAllowedError" || name === "SecurityError")
    return "Camera access was refused. Allow the camera for this site in the browser's settings, then try again.";
  if (name === "NotFoundError" || name === "OverconstrainedError")
    return "No camera was found on this device.";
  if (name === "NotReadableError")
    return "The camera is in use by another app. Close it and try again.";
  return "The camera couldn't be started.";
}

/**
 * Full-screen-on-phone dialog showing the camera. "Take photo" freezes the frame
 * for review; "Use photo" hands a JPEG File to `onCapture` and closes.
 *
 * Props: open (bool), onClose (() => void), onCapture (File => void).
 */
export default function CameraCapture({ open, onClose, onCapture }) {
  const fullScreen = useFullScreenDialog();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facing, setFacing] = useState("environment");
  const [canSwitch, setCanSwitch] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const [shot, setShot] = useState(null);   // { file, url } awaiting Use / Retake

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }, []);

  // (Re)start the stream whenever the dialog opens, the camera is switched, or a
  // shot is retaken. The stream is stopped while a shot is under review, so the
  // camera light doesn't stay on for longer than it needs to.
  useEffect(() => {
    if (!open || shot) return undefined;
    if (!cameraSupported()) {
      setError("This browser can't use the camera from a web page. A secure (https) connection is needed.");
      return undefined;
    }
    let cancelled = false;
    setStarting(true);
    setError("");
    navigator.mediaDevices.getUserMedia({
      audio: false,
      video: { facingMode: { ideal: facing }, width: { ideal: 1920 }, height: { ideal: 1080 } },
    }).then(async stream => {
      if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      // Device labels and counts are only reliable once permission is granted.
      const devices = await navigator.mediaDevices.enumerateDevices().catch(() => []);
      if (!cancelled) setCanSwitch(devices.filter(d => d.kind === "videoinput").length > 1);
    }).catch(err => {
      if (!cancelled) setError(cameraError(err));
    }).finally(() => {
      if (!cancelled) setStarting(false);
    });
    return () => { cancelled = true; stop(); };
  }, [open, facing, shot, stop]);

  // Drop any unused shot when the dialog closes, so the image doesn't sit in memory.
  useEffect(() => {
    if (!open) {
      setShot(s => { if (s) URL.revokeObjectURL(s.url); return null; });
      setError("");
    }
  }, [open]);

  const take = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const scale = Math.min(1, MAX_PHOTO_DIM / Math.max(video.videoWidth, video.videoHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(blob => {
      if (!blob) { setError("The photo couldn't be captured."); return; }
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      const file = new File([blob], `photo-${stamp}.jpg`, { type: "image/jpeg" });
      stop();
      setShot({ file, url: URL.createObjectURL(blob) });
    }, "image/jpeg", JPEG_QUALITY);
  };

  const retake = () => {
    if (shot) URL.revokeObjectURL(shot.url);
    setShot(null);
  };

  const use = () => {
    if (!shot) return;
    const { file, url } = shot;
    URL.revokeObjectURL(url);
    setShot(null);
    onCapture(file);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
        <Box sx={{ flexGrow: 1 }}>Take a photo</Box>
        {canSwitch && !shot && !error && (
          <Tooltip title="Switch camera">
            <IconButton onClick={() => setFacing(f => (f === "environment" ? "user" : "environment"))}>
              <CameraswitchIcon />
            </IconButton>
          </Tooltip>
        )}
      </DialogTitle>
      <DialogContent>
        {error ? (
          <Alert severity="warning">{error}</Alert>
        ) : (
          <Box sx={{
            position: "relative", bgcolor: "black", borderRadius: 1, overflow: "hidden",
            display: "flex", alignItems: "center", justifyContent: "center",
            minHeight: 240, height: fullScreen ? "calc(100% - 32px)" : "auto",
          }}>
            {shot ? (
              <Box component="img" src={shot.url} alt="Captured photo"
                   sx={{ maxWidth: "100%", maxHeight: fullScreen ? "100%" : "65vh", display: "block" }} />
            ) : (
              <Box component="video" ref={videoRef} playsInline muted autoPlay
                   sx={{ maxWidth: "100%", maxHeight: fullScreen ? "100%" : "65vh", display: "block" }} />
            )}
            {starting && !shot && (
              <CircularProgress sx={{ position: "absolute", color: "common.white" }} />
            )}
          </Box>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          The photo goes straight onto the incident and isn't saved to this device.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {shot ? (
          <>
            <Button onClick={retake}>Retake</Button>
            <Button variant="contained" onClick={use}>Use photo</Button>
          </>
        ) : (
          <Button variant="contained" startIcon={<CameraAltIcon />} onClick={take}
                  disabled={!!error || starting}>
            Take photo
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
